import { useCallback, useEffect, useRef, useState } from "react";
import { Boxes, Eye, Layers, ListOrdered, RotateCcw, ScanLine, X } from "lucide-react";
import {
  HOLDER_LABELS,
  scanTarget,
  type DispatchBatchDto,
  type DispatchDto,
  type DispatchHoldersDto,
  type DispatchLine,
  type DispatchPreviewDto,
  type HolderRef,
  type Paginated,
} from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  RowActions,
  SearchInput,
  SelectField,
  SlideOver,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useCan } from "@/auth/access";
import { useQrScanner } from "@/lib/useQrScanner";

import { ListFilters, useListFilters } from "@/components/ListFilters";
const PAGE_SIZE = 20;

const holderKey = (h: Pick<HolderRef, "type" | "id">): string => `${h.type}:${h.id ?? ""}`;
const holderLabel = (h: HolderRef): string => (h.type === "org" ? h.name : `${h.name} (${HOLDER_LABELS[h.type]})`);
const fromKey = (key: string): { type: HolderRef["type"]; id: string | null } => {
  const [type, id] = key.split(":");
  return { type: type as HolderRef["type"], id: id || null };
};
const when = (iso: string): string =>
  new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

/** Rewards → Dispatch (D-20): what moved, and sending more. */
export function DispatchPage() {
  const toast = useToast();
  const can = useCan();
  const canCreate = Boolean(can("dispatch", "create"));
  const canUndo = Boolean(can("dispatch", "delete"));

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<DispatchDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState<DispatchDto | null>(null);
  const [undoing, setUndoing] = useState<DispatchDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      setData(await api.get<Paginated<DispatchDto>>(`/org/dispatch?${query.toString()}`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load dispatches.");
    } finally {
      setLoading(false);
    }
  }, [page, q, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function undo(): Promise<void> {
    if (!undoing) return;
    setBusy(true);
    try {
      await api.post(`/org/dispatch/${undoing.id}/undo`, {});
      toast.success(`${undoing.number} undone.`);
      setUndoing(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not undo this dispatch.");
    } finally {
      setBusy(false);
    }
  }

  const addButton = canCreate ? <Button onClick={() => setComposeOpen(true)}>+ New dispatch</Button> : undefined;

  return (
    <Page bleed>
      <PageHeader title="Dispatch" />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun="dispatch" /> : null}
              {addButton}
            </>
          }
        >
          <SearchInput
            label="Search dispatches"
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search by number, sender or receiver…"
            value={q}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={6} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={addButton}
              message={canCreate ? "Send stock to the next step of your chain." : "Stock sent to you will be listed here."}
              title="No dispatches yet"
            />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>Number</TH>
                <TH>From</TH>
                <TH>To</TH>
                <TH>Items</TH>
                <TH align="right">Units</TH>
                <TH>Date</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((d) => (
                <TR key={d.id} onClick={() => setViewing(d)}>
                  <TD>
                    <span className="font-mono text-xs">{d.number}</span>
                    {d.status === "undone" ? (
                      <span className="ml-2">
                        <Badge tone="warning">Undone</Badge>
                      </span>
                    ) : null}
                  </TD>
                  <TD>{d.from.name}</TD>
                  <TD>{d.to.name}</TD>
                  <TD>
                    <span className="text-muted">
                      {d.lines[0]?.label}
                      {d.lines.length > 1 ? ` +${d.lines.length - 1} more` : ""}
                    </span>
                  </TD>
                  <TD align="right">
                    <span className="tabular-nums">{d.total.toLocaleString("en-IN")}</span>
                  </TD>
                  <TD>{when(d.createdAt)}</TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        { label: "View", icon: Eye, onSelect: () => setViewing(d) },
                        {
                          label: "Undo",
                          icon: RotateCcw,
                          danger: true,
                          hidden: !canUndo || d.status === "undone",
                          onSelect: () => setUndoing(d),
                        },
                      ]}
                      label={d.number}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>

      <SlideOver onClose={() => setViewing(null)} open={Boolean(viewing)} title={viewing?.number ?? ""}>
        {viewing ? (
          <div className="flex flex-col gap-4 text-sm">
            <dl className="grid grid-cols-[6rem_1fr] gap-y-2">
              <dt className="text-muted">From</dt>
              <dd className="text-ink">{holderLabel(viewing.from)}</dd>
              <dt className="text-muted">To</dt>
              <dd className="text-ink">{holderLabel(viewing.to)}</dd>
              <dt className="text-muted">Sent</dt>
              <dd className="text-ink">
                {when(viewing.createdAt)}
                {viewing.createdByName ? ` by ${viewing.createdByName}` : ""}
              </dd>
              {viewing.status === "undone" ? (
                <>
                  <dt className="text-muted">Status</dt>
                  <dd>
                    <Badge tone="warning">Undone</Badge>
                  </dd>
                </>
              ) : null}
            </dl>
            <ul className="divide-y divide-rule/70 rounded-md border border-rule">
              {viewing.lines.map((l, i) => (
                <li className="flex items-center justify-between gap-3 px-3 py-2" key={i}>
                  <span className="min-w-0">
                    <span className="block truncate text-ink">{l.label}</span>
                    {l.productName ? <span className="block text-xs text-muted">{l.productName}</span> : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink">{l.count}</span>
                </li>
              ))}
            </ul>
            <p className="flex justify-between font-medium text-ink">
              <span>Total</span>
              <span className="tabular-nums">{viewing.total.toLocaleString("en-IN")} units</span>
            </p>
          </div>
        ) : null}
      </SlideOver>

      <ConfirmDialog
        loading={busy}
        message={`Move the ${undoing?.total ?? ""} units of ${undoing?.number ?? "this dispatch"} back to ${undoing?.from.name ?? "the sender"}?`}
        onClose={() => setUndoing(null)}
        onConfirm={() => void undo()}
        open={Boolean(undoing)}
        title="Undo dispatch"
      />

      {composeOpen ? (
        <NewDispatch
          onClose={() => setComposeOpen(false)}
          onSent={(d) => {
            setComposeOpen(false);
            toast.success(`${d.number}: ${d.total.toLocaleString("en-IN")} units sent to ${d.to.name}.`);
            void load();
          }}
        />
      ) : null}
    </Page>
  );
}

/* ── new dispatch ────────────────────────────────────────────────── */

type Mode = "scan" | "series" | "carton" | "batch";

const MODES: { id: Mode; label: string; icon: typeof ScanLine }[] = [
  { id: "scan", label: "Scan QR", icon: ScanLine },
  { id: "series", label: "Number series", icon: ListOrdered },
  { id: "carton", label: "Carton", icon: Boxes },
  { id: "batch", label: "Whole batch", icon: Layers },
];

function NewDispatch({ onClose, onSent }: { onClose: () => void; onSent: (d: DispatchDto) => void }) {
  const toast = useToast();
  const [holders, setHolders] = useState<DispatchHoldersDto | null>(null);
  const [fromKeyValue, setFromKeyValue] = useState("org:");
  const [toKeyValue, setToKeyValue] = useState("");
  const [batches, setBatches] = useState<DispatchBatchDto[]>([]);
  const [lines, setLines] = useState<DispatchLine[]>([]);
  const [preview, setPreview] = useState<DispatchPreviewDto | null>(null);
  // The camera is the natural way in on a phone; on a desktop it would only raise a permission prompt.
  const [mode, setMode] = useState<Mode>(() =>
    typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches ? "scan" : "batch",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inputs for the non-scan modes.
  const [seriesBatch, setSeriesBatch] = useState("");
  const [seriesFrom, setSeriesFrom] = useState("");
  const [seriesTo, setSeriesTo] = useState("");
  const [cartonCode, setCartonCode] = useState("");
  const [wholeBatch, setWholeBatch] = useState("");

  const sender = holders?.me ?? fromKey(fromKeyValue);
  const senderParam = holders?.me ? "" : `?type=${sender.type}${sender.id ? `&id=${sender.id}` : ""}`;

  useEffect(() => {
    api
      .get<DispatchHoldersDto>("/org/dispatch/holders")
      .then(setHolders)
      .catch((err: unknown) => setError(err instanceof ApiClientError ? err.message : "Could not load who you can send to."));
  }, []);

  useEffect(() => {
    if (!holders) return;
    api
      .get<DispatchBatchDto[]>(`/org/dispatch/batches${senderParam}`)
      .then(setBatches)
      .catch(() => setBatches([]));
  }, [holders, senderParam]);

  // Every change re-checks the lines against the sender's stock.
  useEffect(() => {
    if (!holders || lines.length === 0) {
      setPreview(null);
      return;
    }
    let live = true;
    api
      .post<DispatchPreviewDto>("/org/dispatch/preview", {
        ...(holders.me ? {} : { from: fromKey(fromKeyValue) }),
        lines,
      })
      .then((res) => {
        if (live) setPreview(res);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiClientError ? err.message : "Could not check these items.");
      });
    return () => {
      live = false;
    };
  }, [holders, lines, fromKeyValue]);

  const add = useCallback(
    (line: DispatchLine) => {
      setError(null);
      setLines((all) => (all.some((l) => JSON.stringify(l) === JSON.stringify(line)) ? all : [...all, line]));
    },
    [],
  );

  // Continuous scanning: the camera stops after each code, so restart it after a beat.
  const lastScan = useRef<{ value: string; at: number } | null>(null);
  const scanner = useQrScanner({
    enabled: mode === "scan",
    onCode: (raw) => {
      const target = scanTarget(raw);
      const now = Date.now();
      if (!(lastScan.current?.value === target.value && now - lastScan.current.at < 3000)) {
        lastScan.current = { value: target.value, at: now };
        add(target.type === "batch" ? { kind: "batch", code: target.value } : { kind: target.type, code: target.value });
      }
      window.setTimeout(() => scanner.retry(), 900);
    },
  });

  const receivers = (holders?.receivers ?? []).filter((r) => holderKey(r) !== holderKey(sender));
  const okLines = lines.filter((_, i) => preview?.lines[i] && !preview.lines[i]!.error);
  const total = preview?.total ?? 0;

  async function send(): Promise<void> {
    if (!toKeyValue) {
      setError("Choose who to send to.");
      return;
    }
    if (okLines.length === 0 || total === 0) {
      setError("Add something from your stock first.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.post<DispatchDto>("/org/dispatch", {
        ...(holders?.me ? {} : { from: fromKey(fromKeyValue) }),
        to: fromKey(toKeyValue),
        lines: okLines,
      });
      onSent(result);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : "Could not send this dispatch.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  const tracked = batches.filter((b) => b.tracked && b.fromSeq !== null);
  const batchOptions = (list: DispatchBatchDto[]) =>
    list.map((b) => ({
      value: b.id,
      label: `${b.label}${b.productName && b.productName !== b.label ? ` · ${b.productName}` : ""} — ${b.inStock} in stock`,
    }));

  return (
    <SlideOver
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button disabled={total === 0 || !toKeyValue} loading={saving} onClick={() => void send()}>
            {total > 0 ? `Dispatch ${total.toLocaleString("en-IN")}` : "Dispatch"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="New dispatch"
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert kind="danger">{error}</Alert> : null}

        {holders && !holders.me ? (
          <SelectField
            label="Send from"
            onChange={(e) => {
              setFromKeyValue(e.target.value);
              setLines([]);
            }}
            options={holders.senders.map((h) => ({ value: holderKey(h), label: holderLabel(h) }))}
            value={fromKeyValue}
          />
        ) : null}
        <SelectField
          hint={holders && receivers.length === 0 ? "There is no one you can send stock to yet." : undefined}
          label="Send to"
          onChange={(e) => setToKeyValue(e.target.value)}
          options={receivers.map((h) => ({ value: holderKey(h), label: holderLabel(h) }))}
          placeholder={holders ? "Choose who receives it" : "Loading…"}
          required
          value={toKeyValue}
        />

        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-ink">Add items</p>
          <div className="grid grid-cols-2 gap-1.5" role="tablist">
            {MODES.map((m) => (
              <button
                aria-selected={mode === m.id}
                className={`inline-flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-[13px] font-medium transition-colors ${
                  mode === m.id ? "border-brand-border bg-brand-light text-brand-ink" : "border-rule text-ink hover:bg-ink/[0.03]"
                }`}
                key={m.id}
                onClick={() => setMode(m.id)}
                role="tab"
                type="button"
              >
                <m.icon aria-hidden="true" className="size-4" />
                {m.label}
              </button>
            ))}
          </div>

          {mode === "scan" ? (
            <div className="relative aspect-[4/3] overflow-hidden rounded-md bg-ink">
              <video className="size-full object-cover" muted playsInline ref={scanner.videoRef} />
              {scanner.state !== "running" ? (
                <div className="absolute inset-0 grid place-items-center px-6 text-center text-sm text-white/85">
                  {scanner.state === "starting"
                    ? "Starting the camera…"
                    : scanner.state === "denied"
                      ? "Allow camera access to scan."
                      : "The camera is not available here. Use another way to add items."}
                </div>
              ) : null}
            </div>
          ) : null}

          {mode === "series" ? (
            <div className="flex flex-col gap-3">
              <SelectField
                hint={tracked.length === 0 ? "No numbered batches in this stock." : undefined}
                label="Batch"
                onChange={(e) => {
                  setSeriesBatch(e.target.value);
                  const b = tracked.find((x) => x.id === e.target.value);
                  setSeriesFrom(b?.fromSeq ? String(b.fromSeq) : "");
                  setSeriesTo(b?.toSeq ? String(b.toSeq) : "");
                }}
                options={batchOptions(tracked)}
                placeholder="Choose a batch"
                value={seriesBatch}
              />
              <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                <TextInput
                  inputMode="numeric"
                  label="From #"
                  onChange={(e) => setSeriesFrom(e.target.value.replace(/\D/g, ""))}
                  value={seriesFrom}
                />
                <TextInput
                  inputMode="numeric"
                  label="To #"
                  onChange={(e) => setSeriesTo(e.target.value.replace(/\D/g, ""))}
                  value={seriesTo}
                />
                <Button
                  disabled={!seriesBatch || !seriesFrom || !seriesTo || Number(seriesTo) < Number(seriesFrom)}
                  onClick={() => add({ kind: "range", batchId: seriesBatch, from: Number(seriesFrom), to: Number(seriesTo) })}
                  variant="secondary"
                >
                  Add
                </Button>
              </div>
            </div>
          ) : null}

          {mode === "carton" ? (
            <form
              className="grid grid-cols-[1fr_auto] items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (cartonCode.trim().length < 4) return;
                add({ kind: "carton", code: scanTarget(cartonCode).value });
                setCartonCode("");
              }}
            >
              <TextInput
                label="Carton code"
                onChange={(e) => setCartonCode(e.target.value)}
                placeholder="Printed under the carton QR"
                value={cartonCode}
              />
              <Button disabled={cartonCode.trim().length < 4} type="submit" variant="secondary">
                Add
              </Button>
            </form>
          ) : null}

          {mode === "batch" ? (
            <div className="grid grid-cols-[1fr_auto] items-end gap-2">
              <SelectField
                hint={batches.length === 0 ? "Nothing in this stock yet." : undefined}
                label="Batch"
                onChange={(e) => setWholeBatch(e.target.value)}
                options={batchOptions(batches)}
                placeholder="Choose a batch"
                value={wholeBatch}
              />
              <Button disabled={!wholeBatch} onClick={() => add({ kind: "batch", batchId: wholeBatch })} variant="secondary">
                Add
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-ink">In this dispatch</p>
          {lines.length === 0 ? (
            <p className="rounded-md border border-dashed border-rule px-3 py-4 text-center text-sm text-muted">
              Nothing added yet.
            </p>
          ) : (
            <ul className="divide-y divide-rule/70 rounded-md border border-rule">
              {lines.map((line, i) => {
                const p = preview?.lines[i];
                return (
                  <li className="flex items-center gap-3 px-3 py-2 text-sm" key={i}>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate ${p?.error ? "text-danger" : "text-ink"}`}>
                        {p?.label ?? lineLabel(line)}
                      </span>
                      <span className={`block text-xs ${p?.error ? "text-danger" : "text-muted"}`}>
                        {p?.error ?? p?.productName ?? "Checking…"}
                      </span>
                    </span>
                    {p && !p.error ? <span className="shrink-0 tabular-nums text-ink">{p.count}</span> : null}
                    <button
                      aria-label="Remove"
                      className="grid size-7 shrink-0 place-items-center rounded text-muted hover:bg-ink/[0.05] hover:text-ink"
                      onClick={() => setLines((all) => all.filter((_, j) => j !== i))}
                      type="button"
                    >
                      <X aria-hidden="true" className="size-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {preview && preview.byProduct.length > 0 ? (
            <div className="flex flex-col gap-1 border-t border-rule pt-2 text-sm">
              {preview.byProduct.map((p) => (
                <p className="flex justify-between font-medium text-ink" key={p.productName}>
                  <span>{p.productName}</span>
                  <span className="tabular-nums">{p.count.toLocaleString("en-IN")} units</span>
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </SlideOver>
  );
}

function lineLabel(line: DispatchLine): string {
  switch (line.kind) {
    case "batch":
      return line.code ? `Batch ${line.code}` : "Batch";
    case "carton":
      return `Carton ${line.code}`;
    case "range":
      return `#${line.from}–#${line.to}`;
    case "code":
      return line.code;
  }
}
